import axios, { type AxiosResponse }           from "axios"
import { useStorage }                          from "vue3-storage"
import { useNotificationStore }                from "@/stores/notification"
import type { ErrorResponse, SuccessResponse } from "@/types/Response"

export default class BaseApiService {

  constructor() {
    const storage = useStorage()

    axios.interceptors.request.use(function (config) {
      const token = storage.getStorageSync("token")

      if (token) {
        config.headers["Authorization"] = "Bearer " + token
      }

      return config
    })

    axios.interceptors.response.use(function (response) {
      const token = storage.getStorageSync("token")
      if (token) {
        const xTokenValidInSeconds = response.headers["x-token-valid-in-seconds"]
        if (xTokenValidInSeconds) {
          const validInSeconds = parseInt(xTokenValidInSeconds)
          storage.setStorageSync("token", token, validInSeconds * 1000)
        }
      }
      return response
    })
  }

  /**
   * Build and return the api-url.
   * @param {string} endpoint The endpoint.
   * @param {string} area The area of the api.
   */
  getApiUrl(endpoint: string, area: string | null = null) {
    return import.meta.env.VITE_API_URL + (area ? "/" + area : "") + endpoint
  }

  /**
   * Build and return the api-url for project-costs.
   * @param {string} endpoint The endpoint.
   */
  getProjectCostsApiUrl(endpoint: string) {
    return this.getApiUrl(endpoint, "projectCosts")
  }

  processRequest(promise: Promise<AxiosResponse<any>>): Promise<SuccessResponse | ErrorResponse> {
    return promise
      .then(async res => {
        if (!res) {
          useNotificationStore().setErrorNotification("Es ist ein Fehler aufgetreten. Bitte laden Sie die Seite neu.")
          return
        }
        return this.processResult(res)
      })
      .catch(error => {
        return this.processErrorResult(error.response)
      })
  }

  /**
   * Process a successful result (status code 200).
   * @param res The result.
   */
  processResult(res): SuccessResponse {
    return {
      success: !!res.data,
      data: res.data,
      headers: res.headers
    }
  }

  /**
   * Process an erroneous result (exception, status code <> 200).
   * @param res The result/error.
   */
  processErrorResult(res): ErrorResponse {
    let errors = []
    let data = res?.data || null

    switch (res?.status) {
      case 401:
      case 403:
        // not authenticated => redirect to login page
        useNotificationStore().setErrorNotification("Sie haben keinen Zugriff auf diese Seite. Bitte loggen Sie sich erneut ein.")
        break
      case 422:
        // validation errors
        errors = res.data
        break
    }
    return {
      success: false,
      data: data,
      headers: res?.headers,
      errors: errors
    }
  }
}
